import { DataTable } from 'primereact/datatable'
import { useNavigate } from 'react-router-dom'
import { Column as TableColumn } from 'primereact/column'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useUserContext } from '../contexts/UserContext'
import { useIsAdminOrRedirectToIndex } from '../../hooks/admin'
import ContextHeader from '../common/ContextHeader'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import { Row } from '../common/pure/FlexContainer'
import Heading from '../common/pure/Heading'
import { pi } from '../common/pure/PrimeIcon'
import TablePagination from '../table/TablePagination'
import {
    ArchiveButton,
    DetailsButton,
    EditButton,
    RestoreButton,
    TableActionContainer,
} from '../table/TableButton'
import Organization from '../../models/Organization'
import t from '../../tools/t'

export default function OrganizationManagementScreen() {
    const navigate = useNavigate()
    const { me, archiveOrganization, restoreOrganization, allOrganizations } = useUserContext()
    const canWriteOrganizations = !!me?.hasAdminPermission(PermissionSuffix.TenantWrite)

    useIsAdminOrRedirectToIndex()

    const navigateToCreateOrganization = () => navigate('/organizations/create')

    const navigateToOrganizationProfile = (org: Organization) =>
        navigate('/organizations/' + org.id)

    const confirmArchiveOrganization = async (organization: Organization) => {
        if (!confirm('Are you sure you want to archive organization: ' + organization.name)) return
        await archiveOrganization(organization.id)
    }

    const confirmRestoreOrganization = async (organization: Organization) => {
        await restoreOrganization(organization.id)
    }

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <Row arrangement="spaced" alignment="start">
                        <ContextHeader>
                            <Heading standalone size={2}>
                                {t('manage_organizations')}
                            </Heading>
                        </ContextHeader>
                        {canWriteOrganizations && (
                            <Button
                                label={t('organization_create')}
                                icon={pi('plus')}
                                onClick={navigateToCreateOrganization}
                            />
                        )}
                    </Row>
                    <Card>
                        <DataTable
                            value={allOrganizations.items}
                            onRowClick={(event) =>
                                navigateToOrganizationProfile(event.data as Organization)
                            }
                            rowClassName={(organization: Organization) =>
                                organization.isArchived ? 'archived-table-row' : undefined
                            }
                            rowHover={true}
                        >
                            <TableColumn header={t('user_field_name')} field="name" />
                            <TableColumn
                                body={(data: Organization) => (
                                    <TableActionContainer>
                                        {canWriteOrganizations &&
                                            (data.isArchived ? (
                                                <RestoreButton
                                                    target={data.id}
                                                    onClick={() => confirmRestoreOrganization(data)}
                                                />
                                            ) : (
                                                <ArchiveButton
                                                    target={data.id}
                                                    onClick={() => confirmArchiveOrganization(data)}
                                                />
                                            ))}
                                    </TableActionContainer>
                                )}
                            />
                        </DataTable>
                        <TablePagination data={allOrganizations} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
